import { useEffect, useState } from 'react'
import {
  applyUpdate,
  checkDeployedBuild,
  registerServiceWorker,
  reloadOnStaleChunk,
  startUpdateLoop,
  updateNotice,
  watchForWaiting,
  type BuildInfo,
  type SwRegistration,
} from '../../lib/appUpdate'
import { formatBuildTime } from '../../lib/text'
import styles from './UpdatePrompt.module.css'

/**
 * QUIL: the page side of the update layer, and the "new version" toast (feature C).
 *
 * The plumbing is the canonical src/lib/appUpdate.ts (app-platform W14). This component is mounted
 * in App.tsx BESIDE the build gate and ABOVE <AuthGate>, so a signed-out visitor gets the worker,
 * the offline landing page and the update offer too. It used to live inside AuthGate, which
 * renders the landing page instead of its children for every signed-out view, so a first visit
 * never registered a worker at all (B4).
 *
 * When enabled (production builds) it:
 *  - registers /sw.js (never throws; no support means no worker),
 *  - re-checks for a new deploy hourly AND when the tab comes back into view (15 min throttle),
 *  - reloads once onto the new build if a lazy chunk from a gone build fails to load (not while
 *    offline: there the chunk is missing from this device, not stale, and a reload eats the click),
 *  - offers the update once a NEW worker is installed and waiting (silent on first install).
 *
 * The toast's words, its one action and its role are the shared contract (`updateNotice`):
 * "A new version of Quil is ready.", Reload, Not now, role="status". Its look is Quil's. The note
 * and build line describe the INCOMING build, read fresh from /version.json (never precached).
 */

export function UpdatePrompt({ enabled = import.meta.env.PROD }: { enabled?: boolean }) {
  const [waitingReg, setWaitingReg] = useState<SwRegistration | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [incoming, setIncoming] = useState<BuildInfo | null>(null)

  useEffect(() => {
    if (!enabled) return
    let live = true
    const disposers: Array<() => void> = [reloadOnStaleChunk()]
    void registerServiceWorker().then((reg) => {
      if (!reg || !live) return
      disposers.push(startUpdateLoop(reg))
      // Read live, so "is this page controlled?" is answered when the offer is due, not now.
      const page = { get controller() { return navigator.serviceWorker?.controller ?? null } }
      disposers.push(watchForWaiting(reg, page, () => {
        if (!live) return
        setDismissed(false)
        setWaitingReg(reg)
      }))
    })
    return () => {
      live = false
      for (const d of disposers) d()
    }
  }, [enabled])

  useEffect(() => {
    if (!waitingReg) return
    let live = true
    // No delay of its own: watchForWaiting only offers a worker that has stayed waiting for
    // SETTLE_MS, and never a kill-switch worker that activates itself, so this fetch cannot race one.
    void checkDeployedBuild(__BUILD_INFO__.buildId).then((check) => {
      if (live && check.state === 'behind') setIncoming(check.deployed)
    })
    return () => { live = false }
  }, [waitingReg])

  if (!waitingReg || dismissed) return null

  const notice = updateNotice('Quil', incoming, formatBuildTime)
  return (
    <div className={styles.prompt} role="status" data-testid="update-prompt">
      <span className={styles.textCol}>
        <span className={styles.text}>{notice.message}</span>
        {notice.note && <span className={styles.whatsNew} data-testid="update-whats-new">{notice.note}</span>}
        {notice.incoming && <span className={styles.incoming} data-testid="update-incoming">{notice.incoming}</span>}
      </span>
      <button
        type="button"
        className={styles.refresh}
        onClick={() => applyUpdate(waitingReg)}
        data-testid="update-refresh"
      >
        {notice.action}
      </button>
      <button type="button" className={styles.later} onClick={() => setDismissed(true)}>
        {notice.dismissLabel}
      </button>
    </div>
  )
}
